/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { useFlags } from '@atlaskit/flag';
import InlineEdit from '@atlaskit/inline-edit';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { CREATE_REQUEST_KEY } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type {
	ActionContext,
	ActionRendererFor,
	ActionRendererKeyToPayload,
	ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';

import { ActionCardFooter, ActionCardWrapper } from '../../../common/ui/action-card-wrapper';
import { useEditorPreset } from '../../jira-issue-create-v2/issue-create-suggestion-preview/issue-description-editor';
import {
	convertFormAnswersToFieldValues,
	createFieldInfoFromResponse,
	isActionWithInputs,
} from '../utils';

import messages from './messages';

const styles = cssMap({
	negativeMarginTop: {
		marginTop: token('space.negative.150'),
	},
	fieldsWrapper: {
		width: '100%',
	},
	fieldLabel: {
		flexShrink: 0,
	},
});

export type JsmCreateRequestFormPayload = {
	summary?: string;
	description?: string;
};

type JsmCreateRequestActionUnifiedRendererProps =
	| ActionRendererFor<typeof ActionKeys.JsmCreateRequest>
	| ActionRendererFor<typeof ActionKeys.JsmCreateRequestV2>
	| ActionRendererFor<typeof ActionKeys.CreateRequest>;

export const JsmCreateRequestActionUnifiedRenderer = ({
	onCustomDataActioned: _onCustomDataActioned,
	actions,
	getStatus,
	getReadonly,
	cloudId,
	ContentRenderer,
	productKey,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: JsmCreateRequestActionUnifiedRendererProps): JSX.Element => {
	di(useFlags);

	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { showFlag } = useFlags();

	const action = actions[0];
	const status = getStatus(action);
	const readonly = getReadonly(action);
	const isActionDisabled = status !== 'idle' || readonly;

	const actionData = isActionWithInputs(action) ? action.data.inputs : action.data;

	// Extract summary and description from either nested requestFieldValues or flat structure for backward compatibility
	const getSummary = () => {
		return actionData.requestFieldValues?.summary || actionData.summary;
	};

	const getDescription = () => {
		return actionData.requestFieldValues?.description || actionData.description;
	};

	// Extract field information from both requestFieldValues and form.answers
	const formFieldValues = actionData.form?.answers
		? convertFormAnswersToFieldValues(actionData.form.answers)
		: {};

	const allFieldValues = {
		...actionData.requestFieldValues,
		...formFieldValues,
	};

	const fieldInfo =
		Object.keys(allFieldValues).length > 0
			? createFieldInfoFromResponse(
					allFieldValues,
					(action.metadata?.requestFieldNames as Record<string, string> | undefined) ||
						actionData.requestFieldNames,
					action.metadata?.formFieldNames as Record<string, string> | undefined,
					action.metadata?.formFieldChoices,
					fg('support_asset_field_for_raise_request')
						? ((action.metadata?.assetNames as Record<string, string> | undefined) ?? {})
						: {},
				)
			: [];

	const [inlineFormData, setInlineFormData] = useState<JsmCreateRequestFormPayload>({
		...(getSummary() && { summary: getSummary() }),
		...(getDescription() && { description: getDescription() }),
	});

	const handleCreateRequest = useCallback(async () => {
		if (isActionDisabled) {
			return;
		}

		try {
			// Create the request with updated summary and description
			// Maintain the original structure - if requestFieldValues exists, update it
			const updatedData = {
				...actionData,
				...(actionData.requestFieldValues
					? {
							requestFieldValues: {
								...actionData.requestFieldValues,
								...(inlineFormData.summary && { summary: inlineFormData.summary }),
								...(inlineFormData.description && { description: inlineFormData.description }),
							},
						}
					: {
							...(inlineFormData.summary && { summary: inlineFormData.summary }),
							...(inlineFormData.description && { description: inlineFormData.description }),
						}),
			};

			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'jsmCreateRequestActionUnifiedRenderer',
				},
				confirmed_tools: {
					message_id: messageId,
					tools: [
						{
							key: action.key,
							tool_id: action.actionId,
							tool_source: action.toolSource,
							invocation_id: action.invocationId,
							data: {
								human_message_id: action.humanMessageId ?? '',
								// The `create-request` tool expects the field values directly as its
								// arguments, whereas the JSM create request tools expect them nested
								// under `inputs`.
								arguments:
									action.key === CREATE_REQUEST_KEY ? updatedData : { inputs: updatedData },
							},
						},
					],
				},
			});
		} catch {
			showFlag({
				id: 'jsm-request-error',
				title: formatMessage(messages.jsmRequestErrorTitle),
				description: formatMessage(messages.jsmRequestErrorDescription),
				appearance: 'error',
				isAutoDismiss: true,
			});
		}
	}, [
		isActionDisabled,
		action,
		actionData,
		inlineFormData,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		showFlag,
		formatMessage,
	]);

	return (
		<Stack space="space.200" xcss={styles.negativeMarginTop}>
			<Text>
				{formatMessage(messages.jsmCreateRequestInstruction, {
					createButtonCopy: <Text as="strong">{formatMessage(messages.createRequestButton)}</Text>,
				})}
			</Text>

			<ActionCardWrapper title={formatMessage(messages.jsmCreateRequestTitle)} product="jsm">
				{/* JSM Request Preview with Inline Editing */}
				<JsmCreateRequestSuggestionPreview
					formData={inlineFormData}
					setFormData={setInlineFormData}
					isDisabled={isActionDisabled}
					action={action}
					productKey={productKey}
					cloudId={cloudId}
					ContentRenderer={ContentRenderer}
					messageId="test-message-id"
				/>

				{fieldInfo.length > 0 && (
					<>
						<Stack space="space.100">
							<Text size="small" color="color.text.subtlest">
								{formatMessage(messages.additionalFieldsLabel)}
							</Text>
							{fieldInfo.map((field) => (
								<Flex key={field.id} direction="column" gap="space.050">
									<Box xcss={styles.fieldLabel}>
										<Text weight="semibold" size="small">
											{field.label}:
										</Text>
									</Box>
									<Box>
										{field.type === 'doc' ? (
											<ContentRenderer
												// eslint-disable-next-line @typescript-eslint/no-explicit-any
												content={field.value as any}
												productKey={productKey}
												siteId={cloudId}
											/>
										) : (
											<Text size="small">
												{field.displayValue || formatMessage(messages.fieldNotSet)}
											</Text>
										)}
									</Box>
								</Flex>
							))}
						</Stack>
					</>
				)}

				{/* Action Buttons */}
				<ActionCardFooter>
					<Button
						type="submit"
						isDisabled={isActionDisabled}
						isLoading={status === 'started'}
						onClick={(_, e) => {
							handleCreateRequest();
							sendAnalyticsEvent(
								e.update({
									actionSubjectId: 'rovoJsmCreateRequestSubmit',
								}),
							);
						}}
						interactionName="ai-mate.jsm-create-request"
					>
						{formatMessage(messages.createRequestButton)}
					</Button>
				</ActionCardFooter>
			</ActionCardWrapper>
		</Stack>
	);
};

type JsmCreateRequestSuggestionPreviewProps = {
	action: ActionWith<
		| ActionRendererKeyToPayload[typeof ActionKeys.JsmCreateRequest]
		| ActionRendererKeyToPayload[typeof ActionKeys.JsmCreateRequestV2]
	>;
	cloudId: string;
	ContentRenderer: JsmCreateRequestActionUnifiedRendererProps['ContentRenderer'];
	formData: JsmCreateRequestFormPayload;
	setFormData: (formData: JsmCreateRequestFormPayload) => void;
	isDisabled: boolean;
};

const JsmCreateRequestSuggestionPreview = ({
	action: _action,
	ContentRenderer,
	formData,
	setFormData,
	isDisabled,
	productKey,
	cloudId,
}: JsmCreateRequestSuggestionPreviewProps & ActionContext) => {
	const { formatMessage } = useIntl();

	const handleOnChange = (
		fieldName: keyof JsmCreateRequestFormPayload,
		fieldValue: string | null,
	) => {
		setFormData({
			...formData,
			[fieldName]: fieldValue,
		});
	};

	const { editorApi, preset } = useEditorPreset({
		productKey,
		siteId: cloudId,
	});

	return (
		<Inline space="space.150">
			<Flex gap="space.150" direction="column" xcss={styles.fieldsWrapper}>
				{formData.summary && (
					<Flex direction="column">
						<Text size="small" color="color.text.subtlest">
							{formatMessage(messages.summaryLabel)}
						</Text>
						<Text>
							{isDisabled ? (
								<span>{formData.summary}</span>
							) : (
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								<InlineEdit<any>
									testId="jsm-create-request-summary-field"
									defaultValue={formData.summary}
									editView={({ ...fieldProps }) => <Textfield {...fieldProps} isCompact />}
									readView={() => <Text>{formData.summary}</Text>}
									onConfirm={(value) => handleOnChange('summary', value)}
									readViewFitContainerWidth
								/>
							)}
						</Text>
					</Flex>
				)}

				{formData.description && (
					<Flex direction="column">
						<Text size="small" color="color.text.subtlest">
							{formatMessage(messages.descriptionLabel)}
						</Text>
						<Text>
							{isDisabled ? (
								<ContentRenderer
									content={formData.description}
									productKey={productKey}
									siteId={cloudId}
								/>
							) : (
								// eslint-disable-next-line @typescript-eslint/no-explicit-any
								<InlineEdit<any>
									testId="issue-create-description-field"
									readViewFitContainerWidth
									defaultValue={formData.description}
									keepEditViewOpenOnBlur={false}
									editView={(fieldProps, ref) => (
										<Box ref={ref}>
											<ComposableEditor
												minHeight={50}
												shouldFocus={true}
												preset={preset}
												maxHeight={160}
												appearance="chromeless"
												onChange={(_ev) => {
													editorApi?.core.actions.requestDocument((doc) => {
														const updatedDoc = doc?.content.length ? doc : null;
														fieldProps.onChange(updatedDoc);
													});
												}}
												onEditorReady={(actions) => {
													if (formData.description) {
														actions.replaceDocument(encodeAdf(formData.description));
													}
												}}
											/>
										</Box>
									)}
									readView={() => (
										<ContentRenderer
											content={formData.description!}
											productKey={productKey}
											siteId={cloudId}
										/>
									)}
									onConfirm={(value) => {
										handleOnChange('description', value);
									}}
								/>
							)}
						</Text>
					</Flex>
				)}
			</Flex>
		</Inline>
	);
};
